<!-- src/views/IntakeForms.vue -->
<template>
  <div class="intake-forms-container">
    <div class="header-container">
      <h2>{{ getSelectedCompany?.name + ' / ' + getSelectedUser?.name }}</h2>
      <div class="toggle-buttons">
        <button
          id="completed-button"
          :class="{ active: status === 'completed', 'fade-animation': fadeStatus === 'completed' }"
          @click="triggerFade('completed')"
        >
          Cases
        </button>
        <button
          id="in-progress-button"
          :class="{ active: status === 'in-progress', 'fade-animation': fadeStatus === 'in-progress' }"
          @click="triggerFade('in-progress')"
        >
          In-Progress
        </button>
        <button
          id="archived-button"
          :class="{ active: status === 'archived', 'fade-animation': fadeStatus === 'archived' }"
          @click="triggerFade('archived')"
        >
          Archived
        </button>
      </div>
    </div>

    <div class="records-box">

    <intake-record
      v-for="record in intakeRecords"
      :key="record.callSid"
      :record="record"
      :status="status"
      :talk-now-disabled="talkNowDisabled"
      @talk-now="handleTalkNow"
      @archive="handleArchive"
      @update:record="handleRecordUpdate"
      @edit-record="handleEditRecord"
    />

    </div>

    <ok-dialog
      v-if="showOkDialog"
      :title="OkDialogTitle"
      :message="OkDialogMessage"
      @close="showOkDialog = false"
    />

    <edit-record-dialog
      v-if="showEditDialog"
      :record="editingRecord"
      @close="showEditDialog = false"
    />

    <phone-number-dialog
      :value="showPhoneDialog"
      :current-phone-number="currentUser ? currentUser.personalPhoneNumber : ''"
      :user-uid="getSelectedUser ? getSelectedUser.uid : ''"
      @input="showPhoneDialog = $event"
      @phone-number-confirmed="handlePhoneNumberConfirmed"
      @canceled="handlePhoneDialogCanceled"
    />

  </div>
</template>

<script>
import axios from 'axios';
import { mapState, mapGetters, mapActions } from 'vuex';
import IntakeRecord from '../components/IntakeRecord.vue';
import OkDialog from '../components/OkDialog.vue';
import { eventService } from '../services/EventService';
import EditRecordDialog from '../components/IntakeForms-EditRecordDialog.vue';
import PhoneNumberDialog from '../components/PhoneNumberDialog.vue';

export default {
  name: 'IntakeForms',
  
  components: {
    IntakeRecord,
    OkDialog,
    EditRecordDialog,
    PhoneNumberDialog
  },

  data() {
    return {
      intakeRecords: [],
      status: 'completed',
      fadeStatus: null,
      showOkDialog: false,
      OkDialogTitle: '',
      OkDialogMessage: '',
      showEditDialog: false,
      editingRecord: null,
      showPhoneDialog: false,
      currentUser: null,
      pendingRecord: null
    };
  },

  watch: {
    showPhoneDialog(newVal, oldVal) {
      console.log('🔴 showPhoneDialog changed from', oldVal, 'to', newVal);
    }
  },

  computed: {
    ...mapState(['user']),
    ...mapGetters(['userRole', 'getSelectedUser', 'getSelectedCompany']),
    talkNowDisabled() {
      return !!this.getSelectedUser?.talkingTo;  // Add optional chaining
    }
  },

  methods: {
    ...mapActions(['setSelectedUser', 'setSelectedCompany', 'updateSelectedCompany', 'updateSelectedUser']),

    handleEditRecord(record) {
      this.editingRecord = record;
      this.showEditDialog = true;
    },

    async handleRecordUpdate(updatedRecord) {
      try {
        // Update the local state
        const recordIndex = this.intakeRecords.findIndex(
          record => record.callSid === updatedRecord.callSid
        );
        
        if (recordIndex !== -1) {
          // Use Vue's reactivity system to update the array
          this.intakeRecords.splice(recordIndex, 1, updatedRecord);
        }
      } catch (error) {
        console.error('Error updating record:', error);
        this.OkDialogTitle = 'Error';
        this.OkDialogMessage = 'An error occurred while updating the record.';
        this.showOkDialog = true;
      }
    },

    handleUsersEvent(event) {
      const data = JSON.parse(event.data);
      delete data._id;
      if (this.getSelectedUser && data.uid == this.getSelectedUser.uid) {
        this.updateSelectedUser(data);
      }
    },

    handleCallsEvent(event) {
      const data = JSON.parse(event.data);
      delete data._id;
      
      // Only process records that match the current view status
      if (data.status === this.status) {
        this.updateOrInsertRecord(data);
      } else {
        // If it's an update to an existing record but status changed,
        // we need to remove it from the current view
        const existingIndex = this.intakeRecords.findIndex(
          record => record.callSid === data.callSid
        );
        if (existingIndex !== -1) {
          this.intakeRecords.splice(existingIndex, 1);
        }
      }
    },

    listenForUsersUpdates() {
      const events = eventService.getConnection(this.getSelectedCompany.companyNumber);
      if (events) {
        events.addEventListener('users', (event) => this.handleUsersEvent(event));
      }
    },

    unListenForUsersUpdates() {
      const selectedCompany = this.getSelectedCompany;
      if (selectedCompany?.companyNumber) {
        const events = eventService.getConnection(this.getSelectedCompany.companyNumber);
        if (events) {
          events.removeEventListener('users', (event) => this.handleUsersEvent(event));
        }
      }
    },

    listenForCallsUpdates() {
      const events = eventService.getConnection(this.getSelectedCompany.companyNumber);
      if (events) {
        events.addEventListener('calls', (event) => this.handleCallsEvent(event));
      }
    },

    unListenForCallsUpdates() {
      const events = eventService.getConnection(this.getSelectedCompany?.companyNumber);
      if (events) {
        events.removeEventListener('calls', (event) => this.handleCallsEvent(event));
      }
    },

    removeCompletedItems() {
      for (let i = this.intakeRecords.length - 1; i >= 0; i--) {
        if (this.intakeRecords[i].status === "completed") {
          this.intakeRecords.splice(i, 1);
        }
      }
    },

    updateOrInsertRecord(newRecord) {
      const index = this.intakeRecords.findIndex(record => record.callSid === newRecord.callSid);
      if (index !== -1) {
        this.intakeRecords[index] = newRecord;
      } else {
        this.intakeRecords.unshift(newRecord);
      }
      // this seems redundant
      // this.removeCompletedItems();
    },

    async handleTalkNow(record) {
      if (!this.getSelectedUser) {
        alert('No user selected. Click Company Account and choose an agent.');
        return;
      }
      if (record.agentOnCall) {
        this.OkDialogTitle = '';
        this.OkDialogMessage = `Agent ${record.agentOnCall} is already talking to this detainee.`;
        this.showOkDialog = true;
        return;
      }
      
      try {
        const response = await axios.get(`/portaldb/db/user/${this.getSelectedUser.uid}`);
        this.currentUser = response.data;
        this.pendingRecord = record;
        this.showPhoneDialog = true;
      } catch (error) {
        console.error('Error fetching user data:', error);
        this.OkDialogTitle = 'Error';
        this.OkDialogMessage = 'An error occurred while fetching user data. Please try again.';
        this.showOkDialog = true;
      }
    },

    async handlePhoneNumberConfirmed() {
      console.log('🔴 handlePhoneNumberConfirmed called');
      // Phone number is already in +1########## format and saved to database in dialog
      // Proceed with the original talk now logic
      await this.setAgentTalkingTo(this.currentUser.uid, this.pendingRecord.callSid);
      
      // Clear temporary state
      this.currentUser = null;
      this.pendingRecord = null;
      console.log('🔴 handlePhoneNumberConfirmed completed');
    },

    handlePhoneDialogCanceled() {
      console.log('🔴 handlePhoneDialogCanceled called');
      // Clear temporary state
      this.currentUser = null;
      this.pendingRecord = null;
      console.log('🔴 handlePhoneDialogCanceled completed');
    },

    async handleArchive(callSid, companyNumber, newStatus) {
      console.log('handleArchive companyNumber: ', companyNumber);
      try {
        await axios.put(`/portaldb/db/updateCallPropertiesWithCompany/${callSid}/${companyNumber}`, {
          status: newStatus
        });
        await this.fetchRecords(this.status);
      } catch (error) {
        console.error('Error updating record status:', error);
        this.OkDialogTitle = 'Error';
        this.OkDialogMessage = 'An error occurred while updating the record status.';
        this.showOkDialog = true;
      }
    },

    triggerFade(status) {
      this.fadeStatus = status;
      setTimeout(() => {
        this.fetchRecords(status);
      }, 200);
    },

    async fetchRecords(status) {
      this.status = status;
      this.fadeStatus = null;
      localStorage.setItem('selectedStatus', status);
      try {
        const companyNumber = this.getSelectedCompany.companyNumber;
        const response = await axios.get(`/portaldb/db/calls/${companyNumber}/${status}`);
        this.intakeRecords = response.data;
      } catch (error) {
        console.error('Error fetching intake records:', error);
        this.OkDialogTitle = 'Error';
        this.OkDialogMessage = 'An error occurred while fetching records.';
        this.showOkDialog = true;
      }
    },

    async setAgentTalkingTo(uid, callSid) {
      try {
        const updateUserObject = { talkingTo: callSid };

        // Update user record talkingTo property
        await axios.put(`/portaldb/db/updateUserProperties/${uid}`, updateUserObject);

        // Update Vuex
        this.updateSelectedUser(updateUserObject);
        
        // Update call record agentOnCall property
        const updateCallObject = {
          agentOnCall: this.getSelectedUser.name,
          jailCallStatus: "Notifying Detainee"
        };

        await axios.put(`/portaldb/db/updateCallProperties/${callSid}`, updateCallObject);        
      } catch (error) {
        console.error('Error in setAgentTalkingTo:', error);
        this.OkDialogTitle = 'Error';
        this.OkDialogMessage = 'An error occurred while updating agent status.';
        this.showOkDialog = true;
      }
    },

    initializeState() {
      const storedStatus = localStorage.getItem('selectedStatus');
      if (storedStatus) {
        this.status = storedStatus;
      }
      this.fetchRecords(this.status);
    }
  },

  mounted() {    
    const view = this.$route.query.view;
    if (view === 'in-progress' || view === 'cases') {
      this.status = view === 'cases' ? 'completed' : 'in-progress';
      // Don't need localStorage in this case
      this.fetchRecords(this.status);
    } else {
      // No valid URL parameter, use normal initialization
      this.initializeState();
    }
    this.listenForUsersUpdates();
    this.listenForCallsUpdates();

    // DEBUG
    window.debugIntakeRecords = () => this.intakeRecords;
  },

  beforeUnmount() {
    this.unListenForUsersUpdates();
    this.unListenForCallsUpdates();
  }
};
</script>

<style scoped>
.intake-forms-container {
  padding: 20px;
}

.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.toggle-buttons button {
  margin-right: 10px;
  padding: 5px 10px;
  border: 2px solid #d2b48c;
  background-color: #f5f5dc;
  cursor: pointer;
  transition: opacity 0.4s ease-in-out;
}

.toggle-buttons button.active {
  background-color: #d2b48c;
}

.toggle-buttons button.fade-animation {
  opacity: 0;
}

.records-box {
  border: 4px solid #d2b48c;
  padding: 20px;
}

#completed-button {
  width: 120px;
}

@media (max-width: 600px) {

  #completed-button, 
  #in-progress-button, 
  #archived-button {
    width: 120px;
    margin-left: 20px;
  }

  .records-box {
    padding: 8px;
  }

}
</style>
